/* eslint-disable max-len */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  WritableSignal,
  signal,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule } from '@angular/forms';
import { Params, Router, RouterModule } from '@angular/router';
import { ProductSearchFilterOptions } from '@app/akita/api/products/models/product-search.filter-options.model';
import { ProductVariant } from '@app/akita/api/products/models/product-variant.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { PopsySkeletonComponent } from '@app/components/skeleton/popsy-skeleton.component';
import { PictureComponent } from '@app/shared/components/picture/picture.component';
import { ApiError } from '@app/shared/models/api/api-error.model';
import { TranslocoModule } from '@ngneat/transloco';
import { map, Observable, of, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { ProductJsonLdComponent } from '../product-json-ld/product-json-ld.component';
import { PriceModel } from '@app/shared/models/api/price.model';
import { ConditionsInfoOverlayDynamicService } from '@app/overlays/conditions-info-overlay/services/conditions-info-overlay-dynamic.service';
import { SentryUtil } from '@app/shared/utils/sentry.util';
import { InformationCircleIconComponent } from '@app/shared/components/icons/generated/information-circle/information-circle.component';
import { AmountPipe } from '@app/shared/pipes/amount.pipe';
import { SpecBatteryIconComponent } from '@app/shared/components/icons/generated/spec-battery/spec-battery.component';
import { CheckCircle1IconComponent } from '@app/shared/components/icons/generated/check-circle-1/check-circle-1.component';
import { Product } from '@app/akita/api/products/models/product.model';
import { BatteryOverlayDynamicService } from '@app/overlays/battery-overlay/services/battery-overlay-dynamic.service';

interface AvailableConditions {
  [key: string]: ProductVariant | null;
}

@Component({
  selector: 'app-product-condition-variants',
  templateUrl: './product-condition-variants.template.html',
  styleUrls: ['./product-condition-variants.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    CommonModule,
    AmountPipe,
    TranslocoModule,
    FlexLayoutModule,
    RouterModule,
    PictureComponent,
    ProductJsonLdComponent,
    PopsySkeletonComponent,
    InformationCircleIconComponent,
    SpecBatteryIconComponent,
    CheckCircle1IconComponent,
  ],
})
export class ProductConditionVariantsComponent implements OnChanges, OnDestroy {
  @Input() public id?: string | null;
  @Input() public model?: string | null;
  @Input() public category?: string | null;
  @Input() public showLabel?: boolean | null = true;

  @ViewChild('conditionButton', { read: ElementRef })
  public conditionButton?: ElementRef | null;

  public readonly biggerThan$: Observable<ScreenSizeInfoState>;
  public readonly isRtl$: Observable<boolean>;

  public showBatteryCondition: WritableSignal<boolean> = signal(false);

  public productOptions$: Observable<ProductSearchFilterOptions | null>;
  public newBatterySelected: WritableSignal<boolean>;

  public variants: Array<ProductVariant>;

  public availableConditions$: Observable<AvailableConditions> = of({});

  public conditionVariants$: Observable<Array<ProductVariant>>;
  public findingConditionVariants$: Observable<boolean>;
  public conditionVariantsError$: Observable<ApiError | null>;

  public readonly queryParamsToKeep$: Observable<Params>;

  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly router: Router,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaProductsService: AkitaProductsService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly conditionsInfoOverlayDynamicService: ConditionsInfoOverlayDynamicService,
    private readonly batteryOverlayDynamicService: BatteryOverlayDynamicService
  ) {
    this.subscriptions = new Subscription();
    this.destroySubject = new Subject();

    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();
    this.isRtl$ = this.akitaRouterQuery.selectIsRtlLayout();

    this.productOptions$ = this.akitaProductsQuery.selectAvailableProductOptions(
      this.model || ''
    );

    this.newBatterySelected = signal(false);
    this.variants = new Array(0);

    this.conditionVariants$ = of([]);

    this.findingConditionVariants$ = of(false);
    this.conditionVariantsError$ = of(null);

    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.id) {
      this.refresh();
    }
  }

  private refresh(): void {
    this.conditionVariants$ = this.akitaProductsQuery
      .selectProductVariants(this.model)
      .pipe(tap((variants: Array<ProductVariant>) => (this.variants = [...variants])));

    this.findingConditionVariants$ =
      this.akitaProductsQuery.selectIsSearchingProductVariants(this.id);

    this.conditionVariantsError$ =
      this.akitaProductsQuery.selectErrorSearchingProductVariants(this.id);

    this.productOptions$ = this.akitaProductsQuery.selectAvailableProductOptions(
      this.model || ''
    );

    this.availableConditions$ = this.conditionVariants$.pipe(
      map((conditionVariants: Array<ProductVariant>) => {
        const conditions: AvailableConditions = {};
        if (conditionVariants) {
          const currentVariant = this.akitaProductsQuery.getProductVariant(
            this.id,
            this.model
          );

          if (currentVariant?.condition.includes('BATTERY') && currentVariant?.inStock) {
            this.newBatterySelected.set(true);
          } else {
            this.newBatterySelected.set(false);
          }

          conditions['EXCELLENT'] = this.akitaProductsQuery.getMatchingConditionVariant(
            this.model,
            currentVariant,
            'EXCELLENT'
          );
          conditions['GOOD'] = this.akitaProductsQuery.getMatchingConditionVariant(
            this.model,
            currentVariant,
            'GOOD'
          );
          conditions['FAIR'] = this.akitaProductsQuery.getMatchingConditionVariant(
            this.model,
            currentVariant,
            'FAIR'
          );
          conditions['FAIR_NEW_BATTERY'] =
            this.akitaProductsQuery.getMatchingConditionVariant(
              this.model,
              currentVariant,
              'FAIR_NEW_BATTERY'
            );
          conditions['GOOD_NEW_BATTERY'] =
            this.akitaProductsQuery.getMatchingConditionVariant(
              this.model,
              currentVariant,
              'GOOD_NEW_BATTERY'
            );
          conditions['EXCELLENT_NEW_BATTERY'] =
            this.akitaProductsQuery.getMatchingConditionVariant(
              this.model,
              currentVariant,
              'EXCELLENT_NEW_BATTERY'
            );

          if (
            conditions['EXCELLENT_NEW_BATTERY']?.inStock ||
            conditions['GOOD_NEW_BATTERY']?.inStock ||
            conditions['FAIR_NEW_BATTERY']?.inStock
          ) {
            this.showBatteryCondition.set(true);
          }

          // redirect if current variant is not in stock
          // prevent Google Bots from getting redirected
          try {
            if (
              !currentVariant?.inStock &&
              this.akitaRouterQuery.isBrowser &&
              (window as any)?.navigator &&
              !(window as any)?.navigator.userAgent.toLowerCase().includes('google')
            ) {
              const cheapestAvailableVariant = conditionVariants
                .sort((a, b) => (a.price?.amount || 0) - (b.price?.amount || 0))
                .find((variant) => variant.inStock);
              const sameColorStorage = conditionVariants.find(
                (variant) =>
                  variant.storage === currentVariant?.storage &&
                  variant.color === currentVariant?.color &&
                  variant.inStock
              );
              const sameStorage = conditionVariants.find(
                (variant) =>
                  variant.storage === currentVariant?.storage && variant.inStock
              );

              // navigate to cheapest available variant
              if (cheapestAvailableVariant || sameColorStorage || sameStorage) {
                this.router
                  .navigate(
                    this.akitaRouterQuery.getProductUrlFragment(
                      sameColorStorage?.id ||
                        sameStorage?.id ||
                        cheapestAvailableVariant?.id ||
                        '',
                      sameColorStorage?.title ||
                        sameStorage?.title ||
                        cheapestAvailableVariant?.title ||
                        ''
                    ),
                    { queryParamsHandling: 'merge' }
                  )
                  .catch((error: unknown) => {
                    console.warn(error);
                  });
              } else {
              }
            } else {
            }
            // this.checkingVariantSubject.next(false);
          } catch (error) {
            console.warn(error);
          }
        }

        return conditions;
      })
    );

    this.changeDetector.markForCheck();
  }

  public getVariantFragment(condition: string | null): Array<string> {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    const aux = this.akitaProductsQuery.getMatchingConditionVariant(
      this.model,
      currentVariant,
      condition
    );

    const out = this.akitaRouterQuery.getProductUrlFragment(
      aux?.id || '',
      aux?.title || ''
    );

    return out;
  }

  public productFragment(id: number | string | null): Array<string> {
    return this.akitaRouterQuery.getProductUrlFragment(`${id}`);
  }

  public onShowConditionInfo(): boolean {
    this.subscriptions.add(
      this.conditionsInfoOverlayDynamicService.observe
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: () => {},
        })
    );

    this.conditionsInfoOverlayDynamicService.open({
      elementRef: this.conditionButton,
      data: { model: this.category },
      isBrowser: this.akitaRouterQuery.isBrowser,
    });

    SentryUtil.addBreadcrumb({
      category: 'ui',
      message: `Opened "Language Overlay" modal`,
      level: 'info',
      type: 'user',
    });

    return false;
  }

  public onShowBatteryInfo($event: any): boolean {
    $event.preventDefault();
    $event.stopPropagation();
    this.subscriptions.add(
      this.batteryOverlayDynamicService.observe
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: () => {},
        })
    );

    this.batteryOverlayDynamicService.open({
      data: {},
      isBrowser: this.akitaRouterQuery.isBrowser,
    });

    SentryUtil.addBreadcrumb({
      category: 'ui',
      message: `Opened "Language Overlay" modal`,
      level: 'info',
      type: 'user',
    });

    return false;
  }

  public getVariantPrice(condition: string | null): PriceModel | null {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    const aux = this.akitaProductsQuery.getMatchingConditionVariant(
      this.model,
      currentVariant,
      condition
    );

    return aux?.price || null;
  }

  public isNewBatteryAvailable(): boolean {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    const aux = this.akitaProductsQuery.getMatchingConditionVariant(
      this.model,
      currentVariant,
      currentVariant?.condition + '_NEW_BATTERY'
    );

    return currentVariant?.condition.includes('BATTERY') || aux?.inStock || false;
  }

  public swithcBatterySelected(): void {
    this.newBatterySelected.set(!this.newBatterySelected());
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    let switchTo: string;
    if (currentVariant?.condition.includes('BATTERY')) {
      switchTo = currentVariant?.condition.replace('_NEW_BATTERY', '');
    } else {
      switchTo = currentVariant?.condition + '_NEW_BATTERY';
    }

    const aux = this.akitaProductsQuery.getMatchingConditionVariant(
      this.model,
      currentVariant,
      switchTo
    );

    this.onSelectVariant(aux?.id || '');
  }

  public checkGoodDeal(): boolean | null {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    const excellent = this.akitaProductsQuery.getMatchingConditionVariant(
      this.model,
      currentVariant,
      'EXCELLENT' + (currentVariant?.condition.includes('BATTERY') ? '_NEW_BATTERY' : '')
    );
    const good = this.akitaProductsQuery.getMatchingConditionVariant(
      this.model,
      currentVariant,
      'GOOD' + (currentVariant?.condition.includes('BATTERY') ? '_NEW_BATTERY' : '')
    );

    if (
      excellent?.inStock &&
      good?.inStock &&
      excellent?.price?.amount &&
      good?.price?.amount &&
      excellent?.price?.amount < good?.price?.amount
    ) {
      return true;
    } else {
      return false;
    }
  }

  public isVariantSelected(condition: string): boolean {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    return currentVariant?.condition === condition && currentVariant?.inStock === true;
  }

  public getTransLabel(value: string): string {
    if (value.includes('EXCELLENT')) {
      return 'label_condition_excelent';
    } else if (value.includes('GOOD')) {
      return 'label_condition_good';
    } else if (value.includes('FAIR')) {
      return 'label_condition_fair';
    } else if (value === 'NEW_BATTERY') {
      return 'action_add_battery';
    }
    return 'label_condition_excelent';
  }

  public onSelectVariant(id: string | null): void {
    const variant = this.akitaProductsQuery.getProductVariant(id, this.model);
    if (id && variant?.inStock) {
      const fragment = this.akitaRouterQuery.getProductUrlFragment(`${id}`);

      this.subscriptions.add(
        this.akitaProductsService.getProduct(`${id}`).subscribe({
          next: (product: Product | null) => {
            if (product) {
              this.router
                .navigate(fragment, { queryParamsHandling: 'merge' })
                .catch((error: unknown) => {
                  console.warn(error);
                });
            }
          },
          error: () => {},
        })
      );
    }
  }

  public trackByProduct(index: number, value?: any | null): string {
    return `${value?.id || index}`;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
